/**
 * Application Version - SINGLE SOURCE OF TRUTH
 * 
 * This file is the ONLY authoritative source for version information.
 * All version displays must import from this file.
 * 
 * DO NOT create duplicate version files.
 * DO NOT hardcode versions elsewhere.
 * 
 * EVENTSFLO-BUILD-VERSION-DISPLAY-REFRESH-V1: Refreshed build/version
 * shown across Dashboard, login, Settings, PWA. Single source = this file.
 */

// App version (semantic versioning)
export const APP_VERSION = "1.29.1";

// Build date (YYYY-MM-DD)
export const BUILD_DATE = "2026-11-02";

// Build timestamp (YYYYMMDD.HHMM) - update on each deployment
// EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1: bumped + brought
// back in sync with sw.js after end-to-end toast verification.
//
// Process note (for future maintainers)
// -------------------------------------
// PRODUCTION DEPLOYS: scripts/bump-build.js runs via the `prebuild` npm
// script and rewrites BOTH this constant AND public/sw.js BUILD_VERSION.
// That gives the SW lifecycle a byte-different worker → triggers
// `updatefound` in registerSw.js → SwUpdateToast.jsx shows the
// "Update available — refresh" toast in every open tab.
//
// PREVIEW / DEV ENV: the CRA dev server hot-reloads JSX without invoking
// `yarn build`, so this constant is NOT rewritten automatically. If a
// user-facing change ships in preview and you want existing browser
// tabs to be notified, manually bump THIS constant AND the matching
// BUILD_VERSION / CACHE_NAME in /app/frontend/public/sw.js. Without
// that, the deployed bundle changes but the SW lifecycle stays quiet.
export const BUILD_TIMESTAMP = "20261102.1135";

// Build commit (short SHA) - injected by scripts/bump-build.js at build time
export const BUILD_COMMIT = "aea02d8d";

// Formatted build version string (with 'v' prefix)
export const BUILD_VERSION = `v${BUILD_TIMESTAMP}`;

// Aliases for backward compatibility
export const PLATFORM_BUILD = BUILD_TIMESTAMP;
export const PWA_BUILD = BUILD_TIMESTAMP;

// Full build info object
export const BUILD_INFO = {
  version: BUILD_VERSION,
  timestamp: BUILD_TIMESTAMP,
  date: BUILD_DATE,
  appVersion: APP_VERSION,
};

// Default export
export default BUILD_VERSION;

export const CHANGES = [
  "EVENTSFLO-BUILD-VERSION-DISPLAY-REFRESH-V1: Build/version refreshed consistently across Dashboard, login, Settings, PWA",
  "Version: 1.28.2 → 1.29.0",
  "EVENTSFLO-PROJECT-INTAKE-TASK-ALIAS-PRECISION-AND-SHIFT-FIELDS-V1: Roads vs Chef Roads disambiguation, alias support, per-shift Location & Description, stronger warning UI",
  "EVENTSFLO-PROJECT-INTAKE-TASK-MATCHING-AND-WARNING-UX-V1: Task description fallback matching, dirty-role cleanup into Notes, initial warning UI bump",
  "EVENTSFLO-PROJECT-INTAKE-DRAFT-CREATE-CLIENT-AND-VENUE-ACTIONS-V1: Inline Create Client / Create Venue from AI Draft review",
  "EVENTSFLO-PROJECT-INTAKE-PASTED-TEXT-INTO-EXISTING-PROJECT-FLOW-V1: Paste-text intake from WhatsApp/SMS/email/notes into existing Project flow",
  "EVENTSFLO-FIX-DUPLICATE-INBOX-ONBOARD-ATTACHMENT-V1: Single-attachment Onboard no longer duplicated",
  "EVENTSFLO-FIX-SUITABILITY-UI-REFRESH-AFTER-CV-UPDATE-V1: Suitability tab now reflects applied CV data",
  "EVENTSFLO-WIRE-CV-EXTRACTION-INTO-SUITABILITY-V1: Languages, experience tags, qualifications additively populated from CV",
  "EVENTSFLO-FIX-IDENTITY-VERIFIED-FRONTEND-REFRESH-V1: Crew Profile reads fresh derived identity_verified",
  "EVENTSFLO-FIX-IDENTITY-VERIFIED-DERIVATION-V1: Identity Verified derived from verified Passport OR verified NID Front+Back",
  "EVENTSFLO-FIX-ASSIGN-DOCUMENT-TO-EXISTING-CREW-SEARCH-V1: Inbox document assignment search returns matches (auth header)",
  "EVENTSFLO-DOCUMENT-VIEWER-PRINT-ACTION-V1: Print PDFs and images directly from the document viewer",
  "EVENTSFLO-INBOX-PDF-BOTTOM-CLIP-FIX-V1: PDF bottom no longer clipped in Inbox Document Review modal",
  "EF-QUOTE-AGGREGATION-LOCK-V1: Hard Lock on Quote Aggregation (CRITICAL)",
  "Part 1: SINGLE SOURCE OF TRUTH - shift_task_line_items ONLY",
  "Part 2: LABOUR RULE - qty = crew × hours, ONE labour line per shift max",
  "Part 3: ZERO FILTER - SKIP if qty <= 0 or total <= 0",
  "Part 4: DUPLICATE PROTECTION - processed_line_ids set",
  "Part 5: STRICT SHIFT LOOP - each shift processed ONCE, NO nested loops",
  "Part 6: HARD VALIDATION - shifts_in_quote == total_project_shifts",
  "Part 7: DEBUG OUTPUT - [QUOTE LOCK] per-shift stats",
  "Part 8: NO 'Crew * 0.00 Hours' fallbacks EVER",
  "Logging: [QUOTE LOCK] prefix for all aggregation logs",
  "Version: 1.28.1 → 1.28.2",
  "EF-SHIFT-LINE-ITEM-STABILITY-FIX-V1: Line Item Stability Fix (CRITICAL)",
  "Backend: GET /shifts/{id}/line-items returns [] on error, never 500",
  "Backend: DELETE /shift-task-line-items/{id} is idempotent (success even if already deleted)",
  "Frontend: loadLineItems() has safe fallback to [] on error",
  "Frontend: handleDeleteItem() does FULL REFRESH from API after delete",
  "Logging: [LINE ITEMS LOAD], [LINE ITEM DELETE] prefixes for debugging",
  "Version: 1.28.0 → 1.28.1",
  "EF-DELETE-TEST-SHIFTS-V1: Safe Test Data Cleanup Tool (COMPLETE)",
  "Endpoint: DELETE /api/admin/test-data/shifts (SuperAdmin only)",
  "Criteria: name contains test/demo/sample/copy (case-insensitive), is_test==True, created_by==system",
  "Cascade: Deletes shift_task_line_items, shift_tasks, shift_crew_assignments",
  "Safety: dry_run=true (default) returns preview without deleting",
  "Logging: [TEST DATA CLEANUP] User, Shifts deleted, Timestamp",
  "Version: 1.27.9 → 1.28.0",
  "EF-SHIFT-DUPLICATION-FIELD-INTEGRITY-V2: Field Integrity Enforcement (COMPLETE)",
  "Part 1: Created canonical build_shift_copy_payload() function - SINGLE SOURCE OF TRUTH",
  "Part 2: Refactored duplicate_shift() to use canonical function",
  "Part 3: Refactored duplicate_shift_multi_day() to use canonical function",
  "Part 4: Added HARD VALIDATION - raises IntegrityError if meeting_place not copied",
  "Part 5: Added [DUPLICATION FIELD CHECK] logging for Original vs Payload meeting_place",
  "Part 6: Fixed 3 existing shifts with missing meeting_place",
  "Version: 1.27.8 → 1.27.9",
  "EF-SHIFT-DUPLICATION-DIAGNOSTIC-V1: Shift Duplication Fix (CRITICAL)",
  "Root cause: duplicate_shift() was not passing company_id to create_shift()",
  "Result: New shifts created with wrong tenant (company_id=00000000-...) and filtered out",
  "Fix: Pass original_company_id explicitly to create_shift()",
  "Fix: Data repair - fixed 26 existing broken shifts with wrong company_id",
  "Logging: Full trace with [DUPLICATE SHIFT] START/END markers",
  "Frontend: Improved toast message showing tasks + crew counts",
  "Frontend: Added console.log for [SHIFT LIST] debugging",
  "Version: 1.27.7 → 1.27.8",
  "EF-PLATFORM-INTEGRITY-ENFORCEMENT-V1: Platform Integrity Enforcement (COMPLETE)",
  "Part 1: Source of Truth enforcement - shift_task_line_items is PRIMARY",
  "Part 2: Data Completeness Guard - validate_project_integrity() before quote generation",
  "Part 3: Shift Duplication Fix - meeting_place, buffers, OOT copied, line_items copied",
  "Part 4: API Response Validation - integrity checks on critical endpoints",
  "Part 5: UI Component Lock (via existing guardrails)",
  "Part 6: Critical Flow Assertions - shifts count validation, totals > 0",
  "Part 7: Debug Endpoints - GET /api/admin/project-integrity/{project_id}",
  "Part 8: Version update - 1.27.6 → 1.27.7",
  "Logging: [DUPLICATE SHIFT], [PROJECT INTEGRITY]",
  "EF-QUOTE-SHIFT-COMPLETENESS-FIX-V1: Quote Shift Completeness Fix (COMPLETE)",
  "Root cause: shift_tasks collection EMPTY - all data in shift_task_line_items",
  "Fix: Complete rewrite of aggregate_shift_tasks_for_financial() to use line items as source",
  "Part 1: Load ALL shifts directly from DB (no pagination limits, limit=10000)",
  "Part 2: Process each shift independently (no data reuse, no loop variable overwrite)",
  "Part 3: Duplicate prevention using processed_shift_ids and processed_line_item_ids sets",
  "Part 4: Correct quantity calculation - crew × hours for labour, qty for supplements",
  "Part 5: Task association fix - line items belong to THAT shift only",
  "Part 6: Travel distance validation - log suspicious high rates that cause €15,000 errors",
  "Part 7: Grouping fix - shifts grouped separately with per-shift subtotals",
  "Part 8: Validation check - assert shifts_in_quote == project_shifts, log error on mismatch",
  "Part 9: Debug mode - GET /api/admin/quote-debug/{project_id} returns full breakdown",
  "Part 10: Version update - 1.27.5 → 1.27.6",
  "Logging: [QUOTE SHIFTS], [SHIFT PROCESS], [SHIFT CALC], [TRAVEL CALC], [QUOTE BUILD V3]",
  "EF-PLATFORM-GUARDRAILS-AND-REGRESSION-CONTROL-V1: Platform Stability (COMPLETE)",
  "Source-of-Truth Contracts: /app/memory/SOURCE_OF_TRUTH_CONTRACTS.md",
  "UI Component Standards: /app/memory/UI_COMPONENT_STANDARDS.md",
  "Change Control Rules: /app/memory/CHANGE_CONTROL_RULES.md",
  "Critical Flows Checklist: /app/memory/CRITICAL_FLOWS_CHECKLIST.md",
  "CSS Baseline Lock: Added PLATFORM UX BASELINE guardrail comments",
  "Backend Guardrails: Added source-of-truth comments to aggregate_shift_tasks_for_financial",
  "Regression Test Suite: /app/backend/tests/test_platform_regression.py",
  "EF-QUOTE-TASK-SOURCE-OF-TRUTH-FIX-V1: Quote Task Source of Truth Fix (COMPLETE)",
  "Quote Build V2: shift_tasks is now the SINGLE SOURCE OF TRUTH for quote generation",
  "Task-First Logic: FOR EACH SHIFT → Load shift_tasks → Build quote lines from tasks",
  "Override Support: shift_task_line_items used only for rate overrides (SECONDARY)",
  "Description Format: '{date} : {task.name} × {hours} Hours' for labour items",
  "Deterministic Build: processed_task_keys prevents duplicates (not line_item_ids)",
  "Orphan Supplements: Auto-generated supplements (meals) still included from line_items",
  "Debug Logging: [QUOTE BUILD V2] and [TASK → LINE] prefixes",
  "Rebuild Endpoint: POST /api/admin/rebuild-quote-data/{project_id} for fixing broken quotes",
  "EF-CREW-OVERLAP-PREVENTION-V1: Crew Overlap Prevention (COMPLETE)",
  "Backend: Comprehensive overlap validation on all assignment endpoints",
  "Backend: Returns HTTP 409 with detailed conflict info on overlap",
  "Backend: Admin override with reason logging to conflict_override_logs",
  "Backend: Audit logging with [OVERLAP CHECK] and [OVERLAP OVERRIDE] prefixes",
  "Frontend: ShiftConflictDialog component for detailed conflict display",
  "Frontend: ShiftPlannerView enhanced to show conflict dialog on drag-drop",
  "Frontend: TaskCrewPlannerPage has full conflict handling (AlertDialog)",
  "EF-QUOTE-DATA-INTEGRITY-FIX-V1: Quote Data Integrity Fix (COMPLETE)",
  "Smart Fallback: name_snapshot reconstructed from task definition, never 'Unknown Task'",
  "Type Inference: type_snapshot inferred from task definition category if missing",
  "Zero-Value Filtering: Line items with 0 quantity and 0 rate excluded from quotes",
  "Duplicate Prevention: Line items tracked by ID to prevent double-counting",
  "Quantity Validation: Ensures crew count is not multiplied incorrectly",
  "Debug Logging: Added [QUOTE BUILD] logs for troubleshooting financial calculations",
  "Data Repair Endpoint: /api/admin/repair-line-item-snapshots for fixing legacy data",
  "EF-CORE-UX-AND-QUOTE-FIX-V1: Core UX and Quote Generation Fix (COMPLETE)",
  "Global UI Baseline: Added comprehensive CSS overrides for form controls visibility",
  "Input/Select/Textarea: Enforced white/light backgrounds with dark readable text",
  "Shadcn/Radix Dropdowns: Explicit background, hover, and selected state styling",
  "React-Select: Full theme-aware styling for searchable dropdowns",
  "React-Datepicker: Header, day cells, selected states with proper contrast",
  "Light/Dark Theme: Both themes now have guaranteed readable form controls",
  "Client Dropdown: Replaced standard select with searchable react-select component",
  "Client Sorting: Backend now returns clients A-Z sorted (case-insensitive collation)",
  "ClientSelect Component: New reusable component at /components/shared/ClientSelect.jsx",
  "Updated Pages: ProjectsPage, QuotesPage, InvoicesPage, MultiShiftImportForm",
  "Quote Generation: Backend try/except and null-safe financial calculations (done prior)",
  "EF-OOT-SUPPLEMENT-PERSISTENCE-FIX-V1: OOT Travel Supplement Rate Persistence Fix (COMPLETE)",
  "Root cause: Rate changes not persisting due to insufficient error handling and logging",
  "Fix: Added explicit number type checking for unit_rate (0 is valid, not falsy)",
  "Fix: Added comprehensive debug logging to trace rate update flow",
  "Backend: Added debug logging in update_shift_task_line_item to track incoming/final values",
  "Frontend: Enhanced handleUpdateTaskRate with explicit type conversion and logging",
  "Frontend: Ensured unit_rate is always sent as a number, never null/undefined",
  "EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Test Data Cleanup & Hide Past Shifts (COMPLETE)",
  "Backend: DELETE /api/admin/test-data/projects endpoint (superadmin only)",
  "Deletes projects with test/demo/sample in name, system-created, or is_test=true",
  "Cascade deletes associated shifts, assignments, tasks, and line items",
  "Frontend: Admin Tools section in System Info page with cleanup button and confirmation modal",
  "Frontend: Hide Past Shifts toggle added to Shift Crew Planner and Shifts List page",
  "Toggle defaults to ON (hides past shifts for cleaner view)",
  "User preference persisted in localStorage (hidePastShifts key)",
  "EF-DOCUMENT-PREVIEW-STANDARDISATION-V2: Document Preview Standardisation (COMPLETE)",
  "Created/enhanced DocumentPreviewModal.jsx with format-based rendering",
  "Supported preview: PDF (iframe), Images JPG/PNG/WEBP/GIF/SVG (img), TXT/CSV (text)",
  "Unsupported files (DOC/DOCX/XLS/XLSX/ZIP): Show fallback with Download button",
  "Backend: Added ?inline=true query param to /api/documents/{id}/download endpoints",
  "Client Portal: Added Preview button alongside Download in crew documents modal",
  "Security: No arbitrary HTML rendering, authenticated endpoints only",
  "EF-AUTH-ERROR-HANDLING-FIX-V1: API Auth Error Handling Fix (COMPLETE)",
  "Fixed: Protected endpoints now return 401/403 instead of 500 for auth failures",
  "Updated ~50 endpoints to use require_auth/require_admin_auth instead of get_current_user",
  "Critical endpoints fixed: /api/documents/download, /api/crew/documents, /api/intake-packets",
  "Backend: Proper HTTPException(401) for missing/invalid tokens, HTTPException(403) for forbidden access",
  "Frontend: Existing Axios interceptor correctly handles 401 → redirect to login",
  "Security: No silent 500 errors masking auth failures, clear error messages",
  "EF-SHIFT-MEETING-PLACE-PWA-V1: Crew PWA Meeting Place Display (COMPLETE)",
  "CrewShiftList: Added meeting_place section with highlighted container and MapPin icon",
  "CrewCalendarView: Added meeting_place to ShiftDetailCard in calendar day modal",
  "Mobile-optimized: whitespace-pre-wrap, break-words for proper text wrapping",
  "Open in Maps: Added Google Maps link for navigation (shown when text > 10 chars)",
  "Empty state: Section hidden when meeting_place is null/empty",
  "EF-SHIFT-MEETING-PLACE-V1: Shift Meeting Place (COMPLETE)",
  "Added meeting_place: Optional[str] field to ShiftBase, ShiftCreate, ShiftUpdate, ShiftBulkCreate models",
  "Admin UI: Textarea field in shift create/edit forms with placeholder and 1000 char limit",
  "Client Portal: Meeting place displayed in ShiftDetailModal with MapPin icon and highlighted box",
  "Field included in all shift API responses, no migration needed (MongoDB flexible schema)",
  "EF-DOCUMENT-ACCESS-READONLY-V1: Document Access (Read-Only) (COMPLETE)",
  "Admin: GET /api/crew/{crew_id}/documents - list crew documents with expiry tracking",
  "Admin: GET /api/documents/{document_id}/download - secure file download with tenant isolation",
  "Admin: GET /api/documents/{document_id}/access-log - view who accessed a document",
  "Admin: GET /api/crew/{crew_id}/documents/access-log - audit all document access for crew",
  "Client: GET /api/client/crew/{crew_id}/documents - view verified docs for assigned crew only",
  "Client: GET /api/client/documents/{document_id}/download - download with project assignment check",
  "Access logging: All views/downloads logged for compliance (user, role, timestamp, IP)",
  "Security: Tenant isolation, role-based access (SuperAdmin > Admin > Client > Crew)",
  "Client Portal: Added 'View Documents' button on crew cards with modal display",
  "EF-CREW-INTAKE-WORKSPACE-V1 (Phase B): Field Conflict Resolution (COMPLETE)",
  "Match detection using email, phone, name similarity (75% threshold for possible, 95% for exact)",
  "Field-by-field conflict comparison table: existing vs incoming values",
  "Admin resolution options: Keep Existing, Use Incoming, Enter Custom Value",
  "Audit logging for all crew profile updates (crew_update_audit collection)",
  "MatchResultsCard component showing exact/possible matches with match reasons",
  "ConflictResolutionDialog for side-by-side comparison and decision making",
  "Safe update workflow: no silent overwrites, user confirms each field change",
  "POST /api/intake-packets/{id}/find-matches - duplicate detection",
  "POST /api/intake-packets/{id}/get-conflicts/{staff_id} - field comparison",
  "POST /api/intake-packets/{id}/resolve-conflicts/{staff_id} - apply decisions",
  "GET /api/crew/{id}/audit-log - view change history",
  "EF-MULTI-TENANT-SCALABILITY-V1: Multi-Tenant Scalability (COMPLETE)",
  "Company-scoped compound indexes on all operational collections",
  "Projects: (company_id, reference_number), (company_id, start_date), (company_id, client_id), (company_id, status)",
  "Shifts: (company_id, reference_number), (company_id, date), (company_id, project_id)",
  "Quotes: (company_id, quote_number), (company_id, status)",
  "Invoices: (company_id, invoice_number), (company_id, status), (company_id, issued_at)",
  "Staff: (company_id, email), (company_id, role), (company_id, status)",
  "Task Definitions: (company_id, name), (company_id, task_type)",
  "CompanyScopeRequiredError for query safety guard",
  "require_company_scope() validation in service layer",
  "Performance verified: scoped queries <2ms on 100k records",
  "EF-EMAIL-SUBJECT-ROUTER-V1: Email Subject Routing (COMPLETE)",
  "Inbound routing: [EF-ESTIMATE], [EF-QUOTE], [EF-INVOICE], [EF-CREDIT], [EF-CREW], [EF-CLIENT], [EF-AUTH]",
  "Outbound emails auto-prefixed with correct [EF-XXX] prefix",
  "POST /api/email/parse-subject, /api/email/generate-subject, /api/email/route-inbound",
  "Email routing logs for audit: GET /api/email/routing-logs",
  "EF-UI-ZOOM-EXTENSION: Zoom Feature Extension (COMPLETE)",
  "Zoom levels: Compact (80%), Normal (100%), Large (120%)",
  "Added to: Projects, Shifts, Crew Admin, Task Definitions pages",
  "Text wrap toggle: ON/OFF for preventing row expansion",
  "Zoom/wrap preferences persist via localStorage",
  "Reusable useZoom hook and ZoomControl component",
  "EF-PARSER-ALIAS-TABLE-V1: Parser Alias Table (COMPLETE)",
  "Global alias normalization table for multilingual parsing (EN/FR/IT)",
  "Accent stripping: é→e, à→a, ç→c for matching",
  "Admin UI: /settings/parser-aliases with CRUD operations",
  "Seeded defaults: 56 French/Italian command, supplement, task aliases",
  "Unicode-safe regex patterns ([A-Za-zÀ-ÿ]) for accented chars",
  "POST /api/parser-aliases/resolve for testing",
  "EF-CREW-SHIFT-BUFFER-V1: Crew Shift Buffer (COMPLETE)",
  "Shift.buffer_before_minutes, buffer_after_minutes, travel_time_hours",
  "Local default: 30/30 min | OOT default: 60/120 min",
  "Travel time adds to buffer_after (+ 30 min disruption margin)",
  "GET /api/assignments/check-conflicts/{staff_id}/{shift_id}",
  "GET /api/staff/{id}/schedule - Schedule with effective times",
  "EF-PARSER-EMAIL-EXTRACTOR-V1: Email Block Extractor (COMPLETE)",
  "Extracts structured commands from emails (ignores greetings, signatures, HTML)",
  "Supports French commands: Projet, Lieu, Effectif, Supplément, etc.",
  "POST /api/projects/quick-extract - Debug extraction only",
  "POST /api/projects/quick-parse-raw - Skip email extraction",
  "EF-QUICK-PROJECT-PARSER-V1: Quick Project Parser (COMPLETE)",
  "POST /api/projects/quick-parse - Parse text to preview",
  "POST /api/projects/quick-create - Create from validated preview",
  "Supports: Client, Project, SHIFT, Venue, Location, Date, Start, Hours, Crew",
  "DUPLICATE * N command for shift repetition with date increment",
  "EF-ACCOUNTING-PERIOD-LOCK-V1: Accounting Period Lock (COMPLETE)",
  "Company.accounting_close_date - documents dated on/before cannot be issued",
  "FinancialPeriodClosedError (HTTP 409, error_type: ACCOUNTING_PERIOD_CLOSED)",
  "Applied to: issue_invoice, issue_credit_note, update_invoice, update_quote",
  "EF-CREDIT-NOTE-UI-V1: Credit Note Frontend UI (COMPLETE)",
  "Invoice Detail: 'Create Credit Note' button for issued/sent/paid invoices",
  "Credit Note Detail Page: view, edit (draft), issue, download PDF",
  "Invoice Balance Display: credited_total_ttc, balance_due_after_credits",
  "Related Credit Notes list on Invoice Detail page",
  "EF-FINANCIAL-AUDIT-LOG-V1: Financial Audit Logging (COMPLETE)",
  "New collection: financial_audit_logs with company_id, user_id, timestamp",
  "Action types: INVOICE_ISSUED, CREDIT_NOTE_ISSUED, PAYMENT_REGISTERED, etc.",
  "GET /api/financial-audit-logs - Query with filters (entity, action, date range)",
  "All financial mutations logged for compliance and traceability",
  "EF-FINANCIAL-STATE-MACHINE-V1: Financial Document State Machine (COMPLETE)",
  "Strict state transitions for Invoice, Credit Note, Quote",
  "Invoice: draft → issued → sent → paid (terminal)",
  "Credit Note: draft → issued (terminal)",
  "Quote: draft → sent → approved/rejected (terminal)",
  "InvalidTransitionError (HTTP 409) for forbidden transitions",
  "validate_invoice_transition(), validate_credit_note_transition(), validate_quote_transition()",
  "EF-CREDIT-NOTES-V1: Credit Note System Backend (COMPLETE)",
  "POST /api/invoices/{id}/credit-notes - Create draft credit note from issued invoice",
  "POST /api/credit-notes/{id}/issue - Issue credit note (CRN-YYYY-NNNNN)",
  "GET /api/credit-notes/{id}/pdf - Generate credit note PDF",
  "GET /api/invoices/{id}/credit-summary - Get invoice balance after credits",
  "Credit notes have negative amounts (credit to customer)",
  "Immutable once issued (locked=true), atomic numbering",
  "VAT summary breakdown preserved from source invoice",
  "EF-GMAIL-PROVIDER-REFORM-01: Gmail Provider Integration (COMPLETE)",
  "New provider type: google_smtp (Google Send Only – App Password)",
  "Auto-configures smtp.gmail.com:587 with STARTTLS",
  "Encrypted App Password storage via Fernet",
  "Password never exposed via API - only smtp_password_set flag",
  "Dynamic form fields based on provider type",
  "Works with existing Provider architecture (Resend, SMTP)",
  "EF-GMAIL-SMTP-01: Gmail SMTP Integration (COMPLETE)",
  "Google (Send Only) provider with App Password support",
  "Encrypted SMTP password storage - never exposed via API",
  "POST /api/email/test-smtp endpoint for connection testing",
  "Error types: AUTH_FAILED, CONNECTION_FAILED, INVALID_SETTINGS, TIMEOUT",
  "Email Services section in Platform Settings",
  "PDF attachments supported via existing email service",
  "EF-GOOGLE-KEY-HARDENING-01: Google Maps API Key Security (COMPLETE)",
  "Encrypted key storage in database using Fernet symmetric encryption",
  "Masked key display (AIza****ABCD) - never exposes full key by default",
  "Admin-only reveal key toggle with 30-second auto-hide",
  "Connection Diagnostics panel with PASS/FAIL status table",
  "Enhanced error classification: API_KEY_MISSING, BILLING_NOT_ENABLED, etc.",
  "Audit logging for key updates and reveals",
  "Security: Key never in logs, browser network, or frontend source",
  "EF-TRAVEL-ADVISOR-03: Google Maps Help + Enable Guidance (COMPLETE)",
  "Help button in Platform Settings > Google Services header",
  "Setup guide modal with Required APIs, Setup Steps, Documentation links",
  "Amber warning banner when Google Maps not configured",
  "External links open in new tab (Google Cloud Console, Distance Matrix docs)",
  "Disclaimer about documentation validity",
  "UI-ONLY enhancement - no backend changes, no financial mutations",
  "EF-TRAVEL-ADVISOR-02: Google Maps API Test Button (COMPLETE)",
  "Test button in Platform Settings > Google Services section",
  "POST /api/travel/advisor/test endpoint for connection validation",
  "Success panel (green) with test route distance/duration results",
  "Failure panel (red) with error type classification",
  "Masked API key handling - never displayed after save",
  "Deterministic test route: Monaco-Ville → Nice Airport",
  "ADVISORY ONLY - no data mutations, no DB writes",
  "EF-TRAVEL-ADVISOR-01: Travel Advisor Advisory Tool (COMPLETE)",
  "Travel Advisor button next to OOT toggle in Shift Edit",
  "POST /api/travel/advisor endpoint for distance/duration lookup",
  "Google Services section in Platform Settings (enable/disable, API key)",
  "Advisory modal shows crew address selector, venue destination",
  "Apply to Travel Tasks button - DOES NOT auto-save",
  "ADVISORY ONLY - no auto-writes, no mutations, user must confirm",
  "EF-FINANCIAL-LOCKDOWN-01: Financial Immutability Hardening (COMPLETE)",
  "Currency/VAT snapshot fields cannot be updated after document creation",
  "Platform setting drift verified - existing quotes unaffected by changes",
  "VAT snapshot isolation verified - old quotes retain original rate",
  "Fixed: generate_quote_from_project now snapshots currency",
  "EF-FINANCIAL-CURRENCY-FOUNDATION-01: Currency Foundation Layer (COMPLETE)",
  "PlatformSettings extended with default_currency_code, default_currency_symbol, default_vat_rate",
  "Quote/Invoice models now snapshot currency at creation (immutable)",
  "PDF rendering uses document-level currency symbol",
  "Frontend displays document-level currency symbol",
  "GET/PUT /api/settings/financial endpoints",
  "Auto-seed financial settings from company country on startup",
  "EF-GLOBAL-COUNTRY-EXT-01: Country Extension (COMPLETE)",
  "Added currency_code (ISO 4217), currency_symbol to Country model",
  "Added timezone_default (IANA format) to Country model",
  "Added phone_code (informational only, NO validation)",
  "Added vat_default_rate (nullable, for future use)",
  "Idempotent seeding updates existing records with new fields",
  "API returns all new fields without breaking changes",
  "EF-GLOBAL-COUNTRY-REF-01: Global ISO-3166 Country Reference (COMPLETE)",
  "New Country model with 194 ISO countries, idempotent seeding",
  "GET /api/countries endpoint returns sorted, active countries",
  "Reusable CountrySelect component with searchable dropdown",
  "Replaced hardcoded country dropdowns in Staff, Company, Venues",
  "Type-to-filter search, keyboard navigation, ISO codes displayed",
  "Preserves existing non-ISO values with visual warning",
  "Unique index on iso2_code prevents duplicates",
  "Crew Allocation Phase 4: Explainability Layer (COMPLETE)",
  "Recommendation tiers: EXCELLENT (80+), GOOD (65-79), ACCEPTABLE (50-64), MARGINAL (<50)",
  "Human-readable explanation_summary derived from score_breakdown",
  "Risk flags: NEAR_WEEKLY_LIMIT, CONSECUTIVE_DAYS, LOW_EXPERIENCE, QUALIFICATION_GAP",
  "All explanations use operational language only (no protected attributes)",
  "Crew Allocation Phase 3: Weighted Scoring Layer (COMPLETE)",
  "8 scoring factors: MET, TaskExp, ClientFam, VenueFam, Tenure, Quals, Pref, Fatigue",
  "Score breakdown with 0-100 scale per candidate",
  "Candidates sorted by total_score descending",
  "Weight version tracking (weight_version_used)",
  "Immutable blocks always get score=0",
  "enable_scoring parameter on allocation-candidates API",
  "Quote/Invoice Engine Structural Redesign (COMPLETE)",
  "PROJECT-level document structure with SHIFT-based grouping",
  "Main Crew Task (Primary Line) always displayed per shift",
  "SUMMARY mode: Associated costs as single grouped line",
  "DETAILED mode: All line items shown individually",
  "Compact print-optimized layout (30-40% reduced whitespace)",
  "Shift headers with date, venue, duration, crew count",
  "Per-shift totals and project-level grand total",
  "API: output_mode parameter on PDF endpoints",
  "Crew Allocation Phase 2: Weight Configuration + Versioning (COMPLETE)",
  "AllocationWeights model with 8 configurable weights and versioning",
  "Default weights: MET(15), TaskExp(25), Client(15), Venue(15), Tenure(10), Quals(10), Pref(5), Fatigue(-5)",
  "Weight normalization logic for Phase 3 scoring",
  "AllocationWeightsChangeLog audit trail for weight changes",
  "New API: GET/POST /allocation/weights, /allocation/weight-versions",
  "Weight activation endpoint: /allocation/weights/{version}/activate",
  "PlatformSettings extended with active_weight_version",
  "Crew Allocation Phase 1: Hard Constraints Engine (COMPLETE)",
  "New AllocationService with 6 hard constraint evaluators",
  "HC-1: Availability check (with travel extension window)",
  "HC-2: Required Qualifications (IMMUTABLE)",
  "HC-3: Role Capability (Crew Chief requirement)",
  "HC-4: Weekly Hours Limit (OVERRIDABLE)",
  "HC-5: Shift Conflict Detection (IMMUTABLE)",
  "HC-6: Employment Status (IMMUTABLE)",
  "Constraint modes: STRICT (default), SHOW_CLOSEST_MATCH, SHOW_CLOSEST_WITH_OVERRIDE",
  "Override logging with audit trail for overridable constraints",
  "New API endpoints: /allocation/status, /shifts/{id}/allocation-candidates",
  "Platform settings extended with allocation_engine_enabled and constraint_mode",
  "Platform-Wide UI Contrast Hardening (COMPLETE)",
  "Replaced all bg-background with bg-surface-base",
  "Replaced all bg-muted with bg-surface-elevated",
  "Replaced all slate-*, gray-*, zinc-* with theme tokens",
  "Removed all dark:* variants (CSS variables handle dark mode)",
  "Added UI token scanner script (yarn lint:ui-tokens)",
  "Standardized surface hierarchy: base → elevated → input → hover",
  "Updated 40+ files for theme consistency",
  "Phase 3: Shift Edit Form with Line Items UX",
  "New ShiftLineItems component with Primary Task + Associated Costs sections",
  "Collapsible Associated Costs table with editable qty/rate",
  "Out of Town toggle for travel supplement suggestions",
  "Live computed totals display",
  "Fixed ShiftsPage runtime error (locations -> venueLocations)",
  "Crew Chief rule updated to use ShiftTaskLineItem architecture",
  "Meal rule updated to use ShiftTaskLineItem architecture",
  "per_crew line items auto-sync when crew_count changes",
  "Phase 2: Unified ShiftTaskLineItem architecture for shift billing",
  "Snapshot policy: name, type, supplement_group captured at creation",
  "Computed totals: quantity × unit_rate (never stored in DB)",
  "Travel time extends crew availability window for conflict checking",
  "Fixed TaskDefinition list not showing all tasks (was limited to 100)",
  "Venue list: collapsible hierarchical view with expand/collapse per venue",
  "Terminology: 'Meeting Point' renamed to 'Location' throughout",
  "Search: filters both venues and locations, auto-expands matching venues",
  "Sorting: venues and locations displayed A-Z (case-insensitive)",
  "Hardened Excel import UI: admin-only visibility, strict .xlsx validation",
  "Updated import confirmation with clearer destructive warning copy",
  "Import result status: green (success), amber (with warnings), red (failed)",
  "Complete state cleanup after import to prevent stale data",
  "Added Excel import endpoint for Venues and VenueLocations (POST /api/venues/import)",
  "Admin-only authentication with X-Admin-API-Key header",
  "Hard reset import: deletes all existing venues/locations before importing",
  "Geolocation parsing support (lat lng or lat,lng format)",
  "Replaced Location with Venue + VenueLocation structure",
  "Implemented cascading dropdowns (Venue → Meeting Point)",
  "Added backend validation for venue/venue_location integrity",
  "Duration is now first-class field for shifts"
];
